import React from 'react';
import {
  Text,
  Notice,
  Link,
  List,
  ListItem,
  DescriptionList,
  Form,
  FormField,
  Input,
  Button,
} from '@heartinternet/hi-ui';
import type { OrderDomainCreditsPageProps } from './types';
import { CreditPricingSection } from './components/CreditPricingSection';
import './OrderDomainCreditsPage.css';

const CGI_URL = '/manage/order-domain-credits.cgi';
const STATEMENT_URL = '/manage/domain_credits_statement.cgi';

const num = (v?: number): number => Number(v) || 0;

export const OrderDomainCreditsPage: React.FC<OrderDomainCreditsPageProps> = ({
  loggedIn = false,
  comSplit = false,
  paypalAllowed = false,
  ukProducts = [],
  comProducts = [],
  transferInProducts = [],
  nominetCredits,
  nominet1yCredits,
  opensrsCredits,
  opensrsComCredits,
  transferInCredits,
  nominet1yLevel,
  opensrsLevel,
  transferInLevel,
  success = false,
  fromPaypal = false,
  complete = false,
}) => {
  // "Your domain credits" balance rows — honour the com_split flag (see Step 1).
  const balanceItems: { label: string; value: React.ReactNode }[] = [];
  if (num(nominetCredits) > 0) {
    balanceItems.push({ label: 'UK (Nominet) 2+ year domain credits', value: num(nominetCredits) });
  }
  balanceItems.push({ label: 'UK (Nominet) domain credits', value: num(nominet1yCredits) });
  if (comSplit) {
    if (num(opensrsCredits) > 0) {
      balanceItems.push({
        label: '.com domain credits (including .info and .biz)',
        value: num(opensrsCredits),
      });
    }
    balanceItems.push({ label: '.com domain credits', value: num(opensrsComCredits) });
  } else {
    balanceItems.push({ label: '.com domain credits', value: num(opensrsCredits) });
  }
  balanceItems.push({ label: 'Transfer-in credits', value: num(transferInCredits) });

  const comDescription = comSplit
    ? 'Use these credits to buy and renew .com, .net, and .org domains.'
    : 'Use these credits to buy and renew .com, .net, .org, .eu, .info and .biz domains.';

  const transferInDescription = comSplit
    ? 'These credits can be used to transfer .com, .net and .org domains from other registrars.'
    : 'These credits can be used to transfer .com, .net, .org, .eu, .info and .biz transfers from other registrars.';

  return (
    <div className="hi-ui-order-domain-credits-page">
      <Text variant="heading-1" id="purchase-dc">
        Domain credits
      </Text>

      {/* Outcome notices */}
      {success && (
        <Notice variant="success" role="alert">
          <Text variant="body" inline>
            Thank you. Please <Link href="/manage/basket.cgi">check out</Link> to complete your
            purchase or select for domain credits below.
          </Text>
        </Notice>
      )}
      {fromPaypal && complete && (
        <Notice variant="success" role="alert">
          <Text variant="body" inline>
            You&apos;ve completed the PayPal purchase process. If the extra credits don&apos;t show
            up below, that just means that we&apos;re waiting for PayPal to clear the payment.
          </Text>
        </Notice>
      )}
      {fromPaypal && !complete && (
        <Notice variant="error" role="alert">
          <Text variant="body" inline>
            You have cancelled the PayPal purchase process.
          </Text>
        </Notice>
      )}

      {/* Intro */}
      <Text variant="body">
        If you are regularly buying domain names, you can take advantage of our lowest domain name
        prices by purchasing domain credits.
      </Text>

      {loggedIn ? (
        <>
          <Text variant="body">
            There are three types &ndash; UK credits that can be used to buy .co.uk, .org.uk, .me.uk
            and .uk domains, .com credits that can be used to buy .com, .net and .org domains, and
            transfer-in credits that can be used to transfer .com, .net and .org domains from other
            registrars.
          </Text>
          <Text variant="body">
            You can buy domain credits in batches of 5, 10, 25, 50 and 100. The more credits you buy
            at one time, the less your domains will cost when you come to buy them. What&apos;s
            more, you can use your credits to both register new domains and renew old ones. Check
            out the prices below for the exact prices.
          </Text>
          <Text variant="body">
            Buying domains using domain credits is easy. You can purchase them using the domain
            search bar and regular shopping cart checkout process on our website.
          </Text>
        </>
      ) : (
        <Text variant="body">
          You can register your domain names by buying online using our{' '}
          <Link href="/manage/domain.cgi">regular shopping basket and checkout system</Link>. You
          can quickly and easily purchase multiple domains for a number of registrants this way.
        </Text>
      )}

      <Text variant="body">
        To get started, purchase some domain credits using the &ldquo;Buy now&rdquo; buttons below.
      </Text>

      {/* Pricing sections */}
      <CreditPricingSection
        heading="Purchase UK (Nominet) domain credits"
        description="Use these credits to buy and renew .co.uk, .org.uk, .me.uk and .uk domains."
        products={ukProducts}
        cgiUrl={CGI_URL}
        paypalAllowed={paypalAllowed}
      />
      <CreditPricingSection
        heading="Purchase .com domain credits"
        description={comDescription}
        products={comProducts}
        cgiUrl={CGI_URL}
        paypalAllowed={paypalAllowed}
      />
      <CreditPricingSection
        heading="Purchase transfer-in credits"
        headingId="purchase-ti"
        description={transferInDescription}
        products={transferInProducts}
        cgiUrl={CGI_URL}
        paypalAllowed={paypalAllowed}
      />

      {loggedIn && (
        <>
          {/* Balances */}
          <section className="hi-ui-order-domain-credits-page__section">
            <Text variant="heading-2">Your domain credits</Text>
            <Text variant="body">Number of domain credits you own:</Text>
            <DescriptionList
              items={balanceItems}
              striped
              layout="horizontal"
              className="hi-ui-order-domain-credits-page__balances"
            />
          </section>

          {/* Low-credit warnings */}
          <section className="hi-ui-order-domain-credits-page__section">
            <Text variant="heading-2" id="low-credit-warn">
              Low domain credits warnings
            </Text>
            <Text variant="body">
              We can warn you automatically when your domain credits are down to a pre-set level.
            </Text>
            <Form
              action={CGI_URL}
              method="post"
              className="hi-ui-order-domain-credits-page__warnings"
            >
              <input type="hidden" name="action" value="set_warning_level" />
              <FormField label="UK domain credits warning level" labelPosition="left">
                <Input
                  type="number"
                  name="nominet_1y_level"
                  min={0}
                  size="sm"
                  defaultValue={num(nominet1yLevel)}
                />
              </FormField>
              <FormField label=".com domain credits warning level" labelPosition="left">
                <Input
                  type="number"
                  name="opensrs_level"
                  min={0}
                  size="sm"
                  defaultValue={num(opensrsLevel)}
                />
              </FormField>
              <FormField label="Transfer-in domain credits warning level" labelPosition="left">
                <Input
                  type="number"
                  name="transferin_level"
                  min={0}
                  size="sm"
                  defaultValue={num(transferInLevel)}
                />
              </FormField>
              <Button
                type="submit"
                variant="primary"
                className="hi-ui-order-domain-credits-page__warnings-submit"
              >
                Update
              </Button>
            </Form>
          </section>

          {/* Statement */}
          <section className="hi-ui-order-domain-credits-page__section">
            <Text variant="heading-2">Domain credits statement</Text>
            <Form action={STATEMENT_URL} method="post">
              <Text variant="body">
                You can view a statement of your domain credits activity below.
              </Text>
              <Button type="submit" variant="primary">
                View statement
              </Button>
            </Form>
          </section>

          {/* Preferences */}
          <section className="hi-ui-order-domain-credits-page__section">
            <Text variant="heading-2">Preferences</Text>
            <Text variant="body">
              You can select options here to affect how transferred or registered domain names are
              handled.
            </Text>
            <List listStyle="disc">
              <ListItem>
                <Link href="/manage/tag-prefs">
                  Select which ips-tag to use for UK domain registrations (HEARTINTERNET or EXTEND)
                </Link>
              </ListItem>
              <ListItem>
                <Link href="/config/domaincp.cgi">
                  Set up the domain control panel and default holding page
                </Link>
              </ListItem>
            </List>
          </section>
        </>
      )}

      {/* Registering domain names (always) */}
      <section className="hi-ui-order-domain-credits-page__section">
        <Text variant="heading-2" id="using-dr">
          Registering domain names
        </Text>
        <Text variant="body">To register and renew domain names now, choose an option below:-</Text>
        <List listStyle="disc">
          <ListItem>
            <Link href="/manage/domain.cgi">Register domains online</Link>
          </ListItem>
          {loggedIn && (
            <ListItem>
              <Link href="/manage/renewals.cgi">Renew domain names</Link>
            </ListItem>
          )}
        </List>
      </section>
    </div>
  );
};
